import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/hooks/useCompany";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Warning, ArrowRight, ShieldCheck, CalendarCheck, Scales, Envelope, Eye, EyeSlash } from "@phosphor-icons/react";

const ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/37e2f85c-af28-4d1e-a11f-be45b52f7e6d/images/73bcabb3aafaad1805e9b0a3d50a86b0c596c21102ecd17e8f911bdd225f3119.jpeg";

const PILLARS = [
  { icon: CalendarCheck, title: "Presenze, ferie e permessi", note: "Tutto sotto controllo." },
  { icon: ShieldCheck, title: "Sicurezza, DPI e attrezzature", note: "Conformità e sicurezza al primo posto." },
  { icon: Scales, title: "Pesi e cottimo", note: "Gestione precisa, risultati chiari." },
];

const BullIcon = () => (
  <svg viewBox="0 0 48 48" className="h-9 w-9" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 12c0 0 1 9 6 12" />
    <path d="M40 12c0 0-1 9-6 12" />
    <path d="M8 12c3-2 7-1 8 2" />
    <path d="M40 12c-3-2-7-1-8 2" />
    <path d="M16 16h16c2 0 4 2 4 5v6c0 6-5 11-12 11s-12-5-12-11v-6c0-3 2-5 4-5Z" />
    <path d="M19 25h.01M29 25h.01" />
    <path d="M21 33c1.5 1.2 4.5 1.2 6 0" />
  </svg>
);

export default function Login() {
  const { login } = useAuth();
  const company = useCompany();
  const nav = useNavigate();
  const saved = localStorage.getItem("bc_login_email") || "";
  const [email, setEmail] = useState(saved);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(!!saved);
  const [error, setError] = useState(
    sessionStorage.getItem("bc-sessione-scaduta")
      ? "Sessione scaduta per inattività: accedi di nuovo." : "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { sessionStorage.removeItem("bc-sessione-scaduta"); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await login(email, password);
    setBusy(false);
    if (remember) localStorage.setItem("bc_login_email", email);
    else localStorage.removeItem("bc_login_email");
    if (r.ok) nav("/");
    else setError(r.error);
  };

  const name = (company.name || "BENEDETTA CARNI").split(" ");
  const first = name[0];
  const rest = name.slice(1).join(" ") || "CARNI";

  return (
    <div className="min-h-screen w-full p-3 sm:p-6" data-testid="login-page"
      style={{ background: "linear-gradient(135deg,#0a2a6b 0%,#123f9e 45%,#2f7ce0 100%)" }}>
      <div className="relative mx-auto max-w-[1400px] overflow-hidden rounded-2xl shadow-2xl"
        style={{ background: "linear-gradient(120deg,#08265f 0%,#12409f 40%,#3b83e6 75%,#8fbdf5 100%)" }}>
        {/* trame decorative */}
        <div className="pointer-events-none absolute inset-0 opacity-25"
          style={{ backgroundImage: "radial-gradient(circle at 78% 8%, rgba(255,255,255,.35) 0, transparent 38%), radial-gradient(circle at 10% 85%, rgba(255,255,255,.18) 0, transparent 45%)" }} />
        <div className="pointer-events-none absolute right-0 top-0 h-52 w-52 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1.4px)", backgroundSize: "14px 14px" }} />

        <div className="relative grid gap-8 p-6 sm:p-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-6">
          {/* colonna identità */}
          <div className="flex flex-col">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-sky-300/70 text-sky-200"
              data-testid="login-bull-icon">
              <BullIcon />
            </div>

            <h1 className="mt-7 font-head font-extrabold leading-[0.92] tracking-tight" data-testid="login-company">
              <span className="block text-4xl sm:text-5xl lg:text-6xl text-white">{first}</span>
              <span className="block text-5xl sm:text-6xl lg:text-7xl text-sky-400">{rest}</span>
            </h1>

            <div className="mt-5 h-[3px] w-16 rounded-full bg-sky-400" />

            <p className="mt-5 max-w-sm text-base md:text-lg font-medium text-white/95">
              Il gestionale pensato per <span className="text-sky-300">semplificare</span> il tuo lavoro.
            </p>

            <img src={ILLUSTRATION} alt="Gestionale su monitor e smartphone" data-testid="login-illustration"
              className="mt-6 hidden w-full max-w-[460px] max-h-[280px] self-start object-contain sm:block"
              style={{ mixBlendMode: "luminosity", opacity: 0.92,
                       WebkitMaskImage: "radial-gradient(ellipse at center, #000 55%, transparent 88%)",
                       maskImage: "radial-gradient(ellipse at center, #000 55%, transparent 88%)" }} />

            {/* pilastri */}
            <div className="mt-auto pt-8">
              <div className="grid gap-4 rounded-xl bg-white/95 p-4 shadow-lg backdrop-blur sm:grid-cols-3 sm:divide-x sm:divide-slate-200"
                data-testid="login-pillars">
                {PILLARS.map((p) => (
                  <div key={p.title} className="flex items-center gap-3 sm:px-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-blue-700">
                      <p.icon size={22} weight="duotone" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-bold leading-tight text-slate-900">{p.title}</div>
                      <div className="text-xs text-slate-500">{p.note}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* pannello form */}
          <div className="flex items-center justify-center">
            <form onSubmit={submit} data-testid="login-form"
              className="w-full max-w-md rounded-2xl bg-white p-7 shadow-2xl sm:p-9">
              <h2 className="font-head text-2xl font-bold tracking-tight text-slate-900">Accedi al gestionale</h2>
              <p className="mt-1.5 text-sm text-slate-500">Usa le credenziali aziendali che ti sono state fornite.</p>

              {error && (
                <div data-testid="login-error"
                  className="mt-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <Warning size={16} className="mt-0.5 shrink-0" /> {error}
                </div>
              )}

              <div className="mt-6 space-y-5">
                <div>
                  <Label htmlFor="email" className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Email</Label>
                  <div className="relative mt-1.5">
                    <Envelope size={19} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-blue-600" />
                    <Input id="email" data-testid="login-email" type="email" required autoComplete="username"
                      value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it"
                      className="h-12 rounded-xl border-slate-200 bg-white pl-11 text-slate-900 shadow-sm transition-colors focus-visible:border-blue-500 focus-visible:ring-blue-500" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Password</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" data-testid="login-password" type={show ? "text" : "password"} required
                      autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 rounded-xl border-slate-200 bg-white pr-11 text-slate-900 shadow-sm transition-colors focus-visible:border-blue-500 focus-visible:ring-blue-500" />
                    <button type="button" data-testid="login-toggle-password" onClick={() => setShow(!show)}
                      aria-label={show ? "Nascondi password" : "Mostra password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-blue-600">
                      {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm text-slate-700">
                  <input type="checkbox" data-testid="login-remember" checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 accent-blue-600" />
                  Ricordami su questo dispositivo
                </label>
              </div>

              <Button type="submit" disabled={busy} data-testid="login-submit"
                className="group mt-6 h-13 w-full rounded-xl bg-blue-700 py-3.5 text-base font-bold text-white shadow-lg shadow-blue-700/25 transition-colors duration-200 hover:bg-blue-800">
                {busy ? "Accesso in corso..." : (<>Accedi <ArrowRight size={18} className="ml-2 transition-transform duration-200 group-hover:translate-x-1" /></>)}
              </Button>

              <button type="button" data-testid="login-forgot"
                onClick={() => setError("Per reimpostare la password rivolgiti al responsabile o all'amministratore.")}
                className="mt-5 text-sm font-semibold text-blue-700 transition-colors hover:text-blue-900 hover:underline">
                Password dimenticata?
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
